import type { Message } from "../db/types";
import type { Config } from "../state/config";
import { OidcError } from "./auth";
import { MOT_BIENVEILLANT } from "./prompt";
import { chatRequest, reasoningParams, RcpError } from "./rcpClient";

/** Keep only the last N messages as context — more isn't useful for rewriting. */
const MAX_HISTORY_MESSAGES = 6;
const REWRITE_TIMEOUT_MS = 30_000;

const REWRITE_SYSTEM = `Tu reformules la question de l'utilisateur en une requête autonome, optimale pour une recherche sémantique dans des documents de conformité RGPD/LPD.

Règles strictes :
- Si la question est déjà claire, complète et autonome, renvoie-la TELLE QUELLE, mot pour mot.
- Si elle contient des références au contexte conversationnel ("celle-ci", "le point 3", "et pour...", "idem"), remplace-les par leur contenu explicite issu de l'historique.
- Si elle est très courte ou télégraphique (ex: "rétention ?", "AES-256 ?"), transforme-la en question complète sans ajouter d'information absente ("Quelles sont les durées de rétention ?", "Le chiffrement AES-256 est-il utilisé ?").
- N'INVENTE RIEN. N'ajoute pas de noms de documents, de vendors, de clauses qui ne seraient pas déjà mentionnés dans la question ou l'historique.
- Conserve la langue de la question (français reste français).
- Ne RÉPONDS PAS à la question. Reformule-la uniquement.
- Maximum une phrase. Pas de guillemets autour. Pas de préambule ("Voici la reformulation :" interdit).

${MOT_BIENVEILLANT}`;

function formatHistory(history: Message[]): string {
  return history
    .slice(-MAX_HISTORY_MESSAGES)
    .map((m) => {
      const who = m.role === "user" ? "Utilisateur" : "Agent";
      return `${who}: ${m.content}`;
    })
    .join("\n\n");
}

/** Reformulate a user question into a standalone query using the conversation history.
 *  Always called — even on the first turn — so short/telegraphic queries get expanded
 *  into full questions that match document vocabulary better.
 *  Falls back to the raw question on any failure. */
export async function rewriteQuery(
  question: string,
  history: Message[],
  cfg: Config,
  externalSignal?: AbortSignal,
): Promise<string> {
  if (!cfg.rewriteModel) return question;

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REWRITE_TIMEOUT_MS);
  const onExternalAbort = () => controller.abort();
  externalSignal?.addEventListener("abort", onExternalAbort);

  try {
    const userContent =
      history.length > 0
        ? `Conversation précédente :\n${formatHistory(history)}\n\nDernière question : ${question}\n\nQuestion reformulée :`
        : `Question : ${question}\n\nQuestion reformulée :`;

    const data = await chatRequest(
      {
        model: cfg.rewriteModel,
        // 200 auparavant. Deux raisons de remonter :
        //   - avec le raisonnement ACTIF (le comportement livre jusqu'ici), les
        //     jetons de reflexion sont comptes dans max_tokens : le modele
        //     consommait les 200 avant d'ecrire, et la requete reformulee
        //     revenait TRONQUEE — voire vide, sans erreur ;
        //   - meme sans raisonnement, une reformulation qui integre l'historique
        //     de conversation peut depasser 200 jetons.
        // Le surcout est nul : seuls les jetons produits sont factures, et sans
        // raisonnement le modele en produit ~20 (mesure du 02.09).
        max_tokens: 2000,
        // Tache MECANIQUE et courte : aucun raisonnement n'y sert. Mesure du
        // 02.09.2026 sur Qwen3.8-27B, meme reecriture rendue dans les trois cas :
        //     sans raisonnement      15 jetons, 1 s
        //     reasoning_effort=low   83 jetons, 1 s
        //     rien                  195 jetons sur 200, 3 s
        // Le temoin frolait le plafond : une question un peu plus longue et il
        // rendait une chaine VIDE, sans erreur. Couper le raisonnement supprime
        // le risque ET divise les jetons par treize.
        //
        // Le DRAPEAU depend de la famille du modele : `enable_thinking: false`
        // coupe chez Qwen mais FUIT chez GLM, ou il fait sortir la reflexion a
        // la place de la reponse. D'ou le helper plutot qu'une valeur en dur.
        ...reasoningParams(cfg.rewriteModel, "coupe"),
        temperature: 0,
        messages: [
          { role: "system", content: REWRITE_SYSTEM },
          { role: "user", content: userContent },
        ],
      },
      cfg,
      controller.signal,
    );

    const raw = data.choices?.[0]?.message?.content?.trim();
    if (!raw) return question;

    return cleanupRewrite(raw, question);
  } catch (err) {
    // If the caller aborted, propagate so the chat turn stops cleanly.
    if (externalSignal?.aborted) throw err;
    // Une panne d'AUTHENTIFICATION n'est pas une panne de reformulation, et la
    // rattraper ici est nuisible. Reformuler est facultatif : si le modèle ne
    // répond pas, la question brute fait très bien l'affaire. Mais un 401 ne dit
    // pas « la reformulation a échoué », il dit « plus rien ne passera » — le
    // tour est condamné, et tout ce qui suit (embeddings, recherche) échouera
    // pour la même raison.
    //
    // En l'avalant, on repoussait l'erreur jusqu'au premier appel qui ne la
    // rattrapait pas, APRÈS que l'interface ait affiché la phase de recherche.
    // L'usager voyait donc le travail se faire, puis un message de connexion.
    // On la laisse remonter : elle arrive au premier appel serveur du tour, et
    // `app.reportError` rouvre le panneau de connexion.
    if (
      err instanceof OidcError ||
      (err instanceof RcpError && (err.status === 401 || err.status === 403))
    ) {
      throw err;
    }
    console.warn("[queryRewrite] failed:", err, "— falling back to raw question");
    return question;
  } finally {
    clearTimeout(timeout);
    externalSignal?.removeEventListener("abort", onExternalAbort);
  }
}

/** Strip common LLM tics (wrapping quotes, leading labels, trailing periods)
 *  from the rewritten question. Returns the original on any weird output. */
function cleanupRewrite(rewritten: string, original: string): string {
  let s = rewritten.trim();
  // Strip a "Question reformulée :" or similar prefix if the model echoed it
  s = s.replace(/^(question\s*(reformul[ée]e|autonome)\s*:?\s*)/i, "").trim();
  // Strip wrapping quotes
  if (
    (s.startsWith('"') && s.endsWith('"')) ||
    (s.startsWith("«") && s.endsWith("»")) ||
    (s.startsWith("'") && s.endsWith("'"))
  ) {
    s = s.slice(1, -1).trim();
  }
  if (s.length < 3) return original;
  if (s.length > 500) return original; // sanity: rewrite shouldn't be much longer
  return s;
}
